// Google Places API (New) city autocomplete for the project Location field
// (#3787). REST, not the Maps JS SDK — the app's CSP script-src doesn't allow
// maps.googleapis.com. Fail-soft contract: every failure (no key, quota,
// outage, bad response) resolves to an empty list, never a throw.

import { ProjectLocationDetails } from "ts-interfaces";

const AUTOCOMPLETE_URL = "https://places.googleapis.com/v1/places:autocomplete";

// Places 400s on empty input; shorter queries are noise anyway. Exported so
// the field can skip the debounce/spinner for queries this would reject.
export const MIN_QUERY_LENGTH = 2;

export interface CitySuggestion {
  placeId: string;
  /** "Austin" — the city itself. */
  primary: string;
  /** "TX, USA" — the disambiguating tail. May be empty. */
  secondary: string;
  /** "Austin, TX, USA" — what we store as the display string. */
  description: string;
}

/** All four keys present, all null. Written when a user types over a picked
 * city, so no stale component survives the deep merge. */
export const EMPTY_LOCATION_DETAILS: ProjectLocationDetails = {
  placeId: null,
  city: null,
  region: null,
  country: null,
};

/** Read at call-time (not module-load) so tests can stub it. */
const placesApiKey = (): string => {
  // Under vitest read ONLY process.env — vi.stubEnv can't reach import.meta.env.
  if (typeof process !== "undefined" && process.env?.VITEST) {
    return process.env.VITE_GOOGLE_PLACES_API_KEY || "";
  }
  let value: string | undefined;
  try {
    value = (import.meta as { env?: Record<string, string | undefined> }).env
      ?.VITE_GOOGLE_PLACES_API_KEY;
  } catch {
    /* import.meta.env unavailable in this context (e.g. plain Node) */
  }
  if (value === undefined && typeof process !== "undefined") {
    value = process.env?.VITE_GOOGLE_PLACES_API_KEY;
  }
  return value || "";
};

/** False when no key is configured — the field renders as plain free text. */
export const placesEnabled = (): boolean => placesApiKey() !== "";

// Bounded session cache of in-flight/settled lookups, keyed by normalized
// query. Caching the PROMISE (not the result) also dedups overlapping
// requests — Places autocomplete is metered, and a backspace-retype inside
// one round-trip would otherwise bill twice.
const CACHE_MAX = 100;
const suggestionCache = new Map<string, Promise<CitySuggestion[]>>();

const toSuggestion = (entry: any): CitySuggestion | null => {
  // queryPrediction entries have no place to attach to — dropped.
  const p = entry?.placePrediction;
  const placeId = p?.placeId;
  if (!placeId) return null;
  const primary = p?.structuredFormat?.mainText?.text || "";
  const secondary = p?.structuredFormat?.secondaryText?.text || "";
  const description =
    p?.text?.text || [primary, secondary].filter(Boolean).join(", ");
  if (!description) return null;
  return { placeId, primary: primary || description, secondary, description };
};

// Throws on any failure so searchCities can evict the cache entry and retry.
const requestCities = async (
  query: string,
  key: string,
): Promise<CitySuggestion[]> => {
  const response = await fetch(AUTOCOMPLETE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Goog-Api-Key": key },
    // "(cities)" type collection; anything it misses is enterable as free text.
    body: JSON.stringify({
      input: query,
      includedPrimaryTypes: ["(cities)"],
      languageCode: "en",
    }),
  });
  if (!response.ok) throw new Error(`places ${response.status}`);
  const data = await response.json();
  return (data?.suggestions || [])
    .map(toSuggestion)
    .filter(Boolean) as CitySuggestion[];
};

/** City suggestions for a partial query. Resolves to `[]` on any failure, and
 * short-circuits before any network call when there's no key or the query is
 * too short. */
export const searchCities = (input: string): Promise<CitySuggestion[]> => {
  const query = input.trim();
  if (query.length < MIN_QUERY_LENGTH) return Promise.resolve([]);
  const key = placesApiKey();
  if (!key) return Promise.resolve([]);

  const cacheKey = query.toLowerCase();
  let pending = suggestionCache.get(cacheKey);
  if (!pending) {
    if (suggestionCache.size >= CACHE_MAX) {
      const oldest = suggestionCache.keys().next().value;
      if (oldest !== undefined) suggestionCache.delete(oldest);
    }
    pending = requestCities(query, key);
    suggestionCache.set(cacheKey, pending);
    // Evict on failure so a transient error retries instead of caching [].
    pending.catch(() => suggestionCache.delete(cacheKey));
  }
  return pending.catch(() => []);
};

/** Structured city from a picked suggestion: last secondary segment is the
 * country, the rest is the region. Deliberately no Place Details round-trip —
 * the autocomplete response already carries everything we store. */
export const detailsFromSuggestion = (
  suggestion: CitySuggestion,
): ProjectLocationDetails => {
  const parts = suggestion.secondary
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  const country = parts.length > 0 ? parts[parts.length - 1] : null;
  const region = parts.length > 1 ? parts.slice(0, -1).join(", ") : null;
  return {
    placeId: suggestion.placeId,
    city: suggestion.primary || null,
    region,
    country,
  };
};
